import {useConfig} from '@aboutyou/config';
import {Head} from '@aboutyou/head-elements';
import {useTranslation} from '@aboutyou/i18n';
import {useOpenedStateManager} from '@aboutyou/opened-state-manager';
import {useHistory, useParams} from '@aboutyou/router';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  PushNotificationIconName,
  RadioButton,
  RadioGroup,
  desktopAndUp,
  mobileOnly,
  tabletAndDown,
  useShowToast,
} from '@aboutyou/ui-components';
import React, {StrictMode, useEffect, useMemo, useState} from 'react';
import styled, {css} from 'styled-components';
import {updateUrlParams} from '../../shared-deprecated/updateUrlParams';
import {DOLFormData, DeclarationOfLossForm} from './DeclarationOfLossForm';
import {DynamicItemForm} from './DynamicItemForm';
import {DOLInfoBottomSheet} from './components/DOLInfoBottomSheet';
import {DOLInfoModal} from './components/DOLInfoModal';
import {useSubmitDeclarationOfLossForm} from './useSubmitDeclarationOfLossForm';

enum Option {
  first,
  second,
}

const PRIMARY_VALUE_SEARCH_KEY = 'primary';
const SECONDARY_VALUE_SEARCH_KEY = 'secondary';
const DECLARATION_OF_LOSS_INFO_MODAL = 'DECLARATION_OF_LOSS_INFO_MODAL';

export const DeclarationOfLossPage = ({
  className,
  addBreak,
}: {
  className?: string;
  addBreak?: boolean;
}) => {
  const {t} = useTranslation();
  const request = useSubmitDeclarationOfLossForm();
  const [mainGroupValue, setMainGroupValue] = useState<Option>(Option.first);
  const {hide, isOpen, open} = useOpenedStateManager(
    DECLARATION_OF_LOSS_INFO_MODAL,
  );
  const [itemIds, setItemIds] = useState<string[] | undefined>();
  const [secondaryGroupValue, setSecondaryGroupValue] = useState<
    Option | undefined
  >(undefined);
  const [showWarningSecondaryGroup, setShowWarningSecondaryGroup] =
    useState<boolean>(false);
  const params = useParams<{orderId: string; ticketId: string}>();
  const history = useHistory();
  const showToast = useShowToast();
  const {device} = useConfig();

  const onFirstRadioGroupChange = (value: Option) => {
    setMainGroupValue(value);
    setSecondaryGroupValue(undefined);

    // update searchParams with options
    // used when user navigates aways and then comes back
    history.replace({
      state: history.location.state,
      search: updateUrlParams({
        search: history.location.search,
        update: new Map([[PRIMARY_VALUE_SEARCH_KEY, [value.toString()]]]),
      }),
    });
  };

  const onSecondRadioGroupChange = (value: Option) => {
    setSecondaryGroupValue(value);
    setShowWarningSecondaryGroup(false);
    history.replace({
      state: history.location.state,
      search: updateUrlParams({
        search: history.location.search,
        update: new Map([[SECONDARY_VALUE_SEARCH_KEY, [value.toString()]]]),
      }),
    });
  };

  useEffect(() => {
    const allParams = new URLSearchParams(history.location.search ?? '');
    const primary = allParams.get(PRIMARY_VALUE_SEARCH_KEY);
    const secondary = allParams.get(SECONDARY_VALUE_SEARCH_KEY);
    if (primary) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setMainGroupValue(
        Option.first.toString() === primary ? Option.first : Option.second,
      );
    }
    if (secondary) {
      setSecondaryGroupValue(
        Option.first.toString() === secondary ? Option.first : Option.second,
      );
    }
  }, []);

  const secondGroupLabels = useMemo(() => {
    switch (mainGroupValue) {
      case Option.first:
        return [
          {label: t("I haven't received the package"), value: Option.first},
          {label: t('I received the package incomplete'), value: Option.second},
        ];

      case Option.second:
        return [
          {label: t('I have returned all items'), value: Option.first},
          {label: t('I have not returned all items'), value: Option.second},
        ];
    }
  }, [mainGroupValue, t]);

  const whereCanYouFindInformationTexts = useMemo(() => {
    const sharedLabels = [
      {
        label: t('Copy the order number from the field below'),
        isHtml: false,
        isTitle: false,
      },
      {
        label: t('Go to your email inbox '),
        isHtml: false,
        isTitle: false,
      },
      {
        label: t(
          'Find the shipping confirmation email by searching with the order number',
        ),
        isHtml: false,
        isTitle: false,
      },
    ];

    if (
      secondaryGroupValue === Option.first &&
      mainGroupValue === Option.first
    ) {
      return [
        {
          label: t('Where can you find the tracking number?'),
          isHtml: false,
          isTitle: true,
        },
        ...sharedLabels,
        {
          label: t(
            'Find the tracking number in the shipping confirmation email',
          ),
          isHtml: true,
          isTitle: false,
        },
      ];
    }
    if (
      secondaryGroupValue === Option.second &&
      mainGroupValue === Option.first
    ) {
      return [
        {
          label: t('Where can you find the tracking and item number?'),
          isHtml: false,
          isTitle: true,
        },
        ...sharedLabels,
        {
          label: t(
            'Find the tracking and item number in the shipping confirmation email',
          ),
          isHtml: true,
          isTitle: false,
        },
      ];
    }
    if (
      secondaryGroupValue === Option.second &&
      mainGroupValue === Option.second
    ) {
      return [
        {
          label: t('Where can you find the item number?'),
          isHtml: false,
          isTitle: true,
        },
        ...sharedLabels,
        {
          label: t('Find the item number in the shipping confirmation email'),
          isHtml: true,
          isTitle: false,
        },
      ];
    }
  }, [secondaryGroupValue, t, mainGroupValue]);

  const submitInformations = async (data: DOLFormData) => {
    try {
      await request.mutateAsync({
        ticketId: params.ticketId,
        orderNumber: data.orderNumber,
        trackingNumber: data.trackingNumber,
        dateOfDispatch: new Date(''), // not needed anymore as input field was removed
        userPersonalDetails: {
          name: data.name,
          surname: data.surname,
          dateOfBirth: new Date(data.dateOfBirth),
          emailAddress: data.email,
          street: data.street,
          houseNumber: data.houseNumber,
          postalCode: data.postalCode,
          city: data.city,
        },
        case:
          mainGroupValue === Option.first
            ? {
                $case: 'delivery',
                delivery: {missingItems: itemIds || []},
              }
            : {$case: 'return', return: {returnedItems: itemIds || []}},
      });
      history.push('/');
      showToast({
        content: t('You have successfully submitted Declaration of loss!'),
        type: 'SUCCESS',
      });
    } catch (e) {
      // - `OK` -> Success Message
      // - `Not Found` (5) or `Invalid Argument` (3) -> Invalid/Missing Ticket or Order ID
      // - `Already Exists` (6) -> Form has already been submitted
      // - Other -> Technical Difficulties
      switch (e.code) {
        case 5:
        case 3:
          showToast({
            content: t(
              'Unfortunately, we cannot open the requested link. Please reach out to customer service again.',
            ),
            type: 'ERROR',
          });
          history.push('/');
          break;
        case 6:
          showToast({
            content: t(
              'You have already successfully submitted the from. If you need further assistance, please reach out to our Support Team',
            ),
            type: 'ERROR',
          });
          history.push('/');
          break;

        default:
          showToast({
            content: t(
              'Oops, there is a technical issue. Please try again later.',
            ),
            type: 'ERROR',
          });

          break;
      }
    }
  };

  return (
    <StrictMode>
      <Head>
        <title>{t('Declaration of Loss')}</title>
        <meta data-rh="true" content="noindex, nofollow" name="robots"></meta>
      </Head>
      <PageWrapper className={className}>
        <ElementWrapper>
          <MainTitle>{t('Declaration of Loss')}</MainTitle>
          <DescriptionWrapper>
            <TextElement>{t('Hello,')}</TextElement>
            {addBreak && <br />}
            <TextElement>
              {t(
                'Thank you for taking a moment to complete all the required details in this declaration. The information you provide will help us to handle your request as soon as possible.',
              )}
            </TextElement>
          </DescriptionWrapper>
          <FormWrapper>
            <DOLRadioGroup label={t('What is it about?')}>
              <StyledRadioButton
                name="main-group"
                label={t('Delivery')}
                value={Option.first}
                boxedMode
                iconName={PushNotificationIconName.Truck}
                checked={mainGroupValue === Option.first}
                onChange={() => onFirstRadioGroupChange(Option.first)}
              />
              <StyledRadioButton
                name="main-group"
                label={t('Return')}
                value={Option.second}
                boxedMode
                iconName={GenericIconName.ParcelBox}
                checked={mainGroupValue === Option.second}
                onChange={() => onFirstRadioGroupChange(Option.second)}
              />
            </DOLRadioGroup>

            <DOLRadioGroup
              label={t('Please select')}
              $showDefaultAsColumn={true}
            >
              {showWarningSecondaryGroup && (
                <WarningText>
                  {t('Please select one of the options below.')}
                </WarningText>
              )}
              <RadioButtonsWrapper>
                {secondGroupLabels.map(item => (
                  <StyledRadioButton
                    key={item.label + item.value}
                    name="secondary-group"
                    label={item.label}
                    value={item.value}
                    boxedMode
                    checked={secondaryGroupValue === item.value}
                    onChange={() => onSecondRadioGroupChange(item.value)}
                  />
                ))}
              </RadioButtonsWrapper>
            </DOLRadioGroup>
            {mainGroupValue === Option.second && (
              <div>
                <Title>{t('Where can you find the information?')}</Title>
                <TextElement>
                  {t(
                    'You can find your tracking number of your return on the shipping receipt from your selected logistics company.',
                  )}
                </TextElement>
              </div>
            )}

            {whereCanYouFindInformationTexts &&
              whereCanYouFindInformationTexts.length > 0 && (
                <div>
                  <Title>{whereCanYouFindInformationTexts[0]!.label}</Title>
                  <InstructionList type="1">
                    {whereCanYouFindInformationTexts.map((item, index) => {
                      if (item.isTitle) {
                        return null;
                      }

                      if (item.isHtml) {
                        return (
                          <TextElement key={item.label}>
                            {`${index}. ${item.label}`}
                            <button
                              data-testid="info-icon"
                              type="button"
                              onClick={() => {
                                open();
                              }}
                            >
                              <StyledIcon
                                asset={GenericIconName.Info}
                                size="XS"
                              />
                            </button>
                          </TextElement>
                        );
                      }

                      return (
                        <TextElement
                          key={item.label}
                          dangerouslySetInnerHTML={{
                            __html: `${index}. ${item.label}`,
                          }}
                        />
                      );
                    })}
                  </InstructionList>
                </div>
              )}

            {secondaryGroupValue === Option.second && (
              <DynamicItemForm onSubmit={setItemIds} />
            )}

            <WarningWrapper>
              <Title>{t('Personal information')}</Title>
              <TextElement>{t('*Required fields')}</TextElement>
            </WarningWrapper>

            {device === 'desktop' && (
              <DOLInfoModal
                isOpen={isOpen}
                onClose={() => {
                  hide();
                }}
                showItemNumberArrow={
                  (mainGroupValue === Option.second &&
                    secondaryGroupValue === Option.second) ||
                  (mainGroupValue === Option.first &&
                    secondaryGroupValue === Option.second)
                }
                showTrackingNumberArrow={mainGroupValue === Option.first}
              />
            )}
            {device === 'mobile' && (
              <DOLInfoBottomSheet
                isOpen={isOpen}
                onClose={() => {
                  hide();
                }}
                showItemNumberArrow={
                  (mainGroupValue === Option.second &&
                    secondaryGroupValue === Option.second) ||
                  (mainGroupValue === Option.first &&
                    secondaryGroupValue === Option.second)
                }
                showTrackingNumberArrow={mainGroupValue === Option.first}
              />
            )}

            <DeclarationOfLossForm
              onSubmit={submitInformations}
              orderId={params.orderId}
              isValidToSubmit={!showWarningSecondaryGroup}
              validateSecondaryGroup={() => {
                if (secondaryGroupValue === undefined) {
                  setShowWarningSecondaryGroup(true);
                }
              }}
              showCopyButtonOnOrderNumber={
                !(
                  mainGroupValue === Option.second &&
                  secondaryGroupValue === Option.first
                )
              }
            />
          </FormWrapper>
        </ElementWrapper>
      </PageWrapper>
    </StrictMode>
  );
};

const StyledIcon = styled(Icon)`
  display: inline-block;
`;

const InstructionList = styled.ol`
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PageWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 46px;
`;

const ElementWrapper = styled.div`
  max-width: 524px;
`;

const StyledRadioButton = styled(RadioButton)`
  span {
    color: ${COLORS.Black};
  }
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.White};
  border: 1px solid ${COLORS.Grey3};
  padding: 16px;
  ${desktopAndUp(css`
    border-radius: 4px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.04),
      0 10px 15px 0 rgba(0, 0, 0, 0.04);
    gap: 24px;
    padding: 32px;
  `)}

  & a {
    text-decoration: underline;
  }
`;

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${mobileOnly(css`
    margin: 0px 16px;
  `)}

  ${desktopAndUp(css`
    margin: 16px 0 40px 0;
  `)}
`;

const DOLRadioGroup = styled(RadioGroup)<{$showDefaultAsColumn?: boolean}>`
  ${props =>
    props.$showDefaultAsColumn
      ? css`
          flex-direction: column;
          gap: 8px;
        `
      : ''}

  ${mobileOnly(css`
    flex-direction: column;
  `)}

  ${tabletAndDown(css`
    gap: 8px;
    fieldset & {
      margin-bottom: 40px;
    }
  `)}

  > legend {
    font-size: ${FONT_SIZES.px16};
    font-weight: ${FONT_WEIGHTS.mediumHeavy};
    margin-bottom: 8px;
    margin-left: 0;
  }

  > label {
    flex: 1 auto;

    ${mobileOnly(css`
      &:not(:last-child) {
        margin-right: 0;
      }
    `)}

    > div {
      min-width: 20px;
    }
  }

  span {
    font-weight: ${FONT_WEIGHTS.mediumBold};
  }
`;

const RadioButtonsWrapper = styled.div`
  display: flex;
  ${mobileOnly(css`
    flex-direction: column;
  `)}

  & > label:not(:last-child) {
    margin-right: 20px;
  }

  ${tabletAndDown(css`
    gap: 8px;
  `)}

  > legend {
    font-size: ${FONT_SIZES.px16};
    font-weight: ${FONT_WEIGHTS.mediumHeavy};
    margin-bottom: 8px;
    margin-left: 0;
  }

  > label {
    flex: 1 auto;

    ${mobileOnly(css`
      &:not(:last-child) {
        margin-right: 0;
      }
    `)}

    > div {
      min-width: 20px;
    }
  }

  span {
    font-weight: ${FONT_WEIGHTS.mediumBold};
  }
`;

const WarningWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.h2`
  font-size: ${FONT_SIZES.px16};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  ${tabletAndDown(css`
    margin-bottom: 0px;
  `)}
  margin-bottom:8px;
`;

const TextElement = styled.p`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 20px;
  display: inline-block;

  span {
    cursor: pointer;
  }

  button {
    background-color: unset;
    border: unset;
    margin-left: 8px;
    display: inline-flex;
  }

  ${tabletAndDown(css`
    margin-bottom: 8px;
    &:not(:first-child) {
      margin-top: 0;
    }

    button {
      margin-left: 0;
    }

    &:last-child {
      margin-bottom: 20px;
    }
  `)}
`;

const MainTitle = styled.h1`
  ${tabletAndDown(css`
    margin-bottom: 1rem;
  `)}

  ${mobileOnly(css`
    margin: 0px 1rem;
    font-size: ${FONT_SIZES.px24};
    font-weight: ${FONT_WEIGHTS.mediumHeavy};
  `)}
`;

const WarningText = styled.p`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  color: ${COLORS.Red};
  margin: 0;
`;
